import { useEffect, useMemo, useState } from "react";
import { useRegistration } from "@/context/RegistrationContext";
import { sendParentPhoneOtp, verifyParentPhoneOtp, verifyApplication } from "@/lib/api";
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from "@/components/ui/input-otp";
import { ArrowRight, CheckCircle2, AlertCircle, Timer, MailCheck, PencilLine } from "lucide-react";
import { toast } from "sonner";

const onlyDigits = (v, max = 15) => (v || "").replace(/\D+/g, "").slice(0, max);
const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());
const phoneValid = (p) => /^\d{10,15}$/.test((p || "").trim());

const REQ = "This field is required";
const PHONE_REQ = "Enter a valid phone (10–15 digits)";

const RequiredStar = () => <span className="text-red-500 ml-1">*</span>;

const ErrText = ({ showErrors, errors, k }) =>
  showErrors && errors[k] ? (
    <div className="flex items-center gap-1 text-[11px] text-red-600 mt-1" data-testid={`err-${k}`}>
      <AlertCircle className="h-3 w-3" /> {errors[k]}
    </div>
  ) : null;

// Plain email input — no OTP verification (email delivery isn't reliable right
// now). The candidate just types it and is asked to double-check it themselves.
function EmailField({ role, label, value, onChange, showErrors, requiredError, required = true }) {
  return (
    <div data-field={`email-${role}`}>
      <label className="mu-label" htmlFor={`email-${role}`}>{label}{required && <RequiredStar />}</label>
      <input
        id={`email-${role}`}
        type="email"
        data-testid={`input-email-${role}`}
        className="mu-input"
        placeholder="you@example.com"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <ErrText showErrors={showErrors} errors={{ [role]: requiredError }} k={role} />
    </div>
  );
}

// Self-contained send/verify widget for one parent's phone number — mirrors
// EmailField exactly, just over SMS instead of email.
function PhoneField({ role, label, value, onChange, verified, onVerified, onUnverify, applicationNumber, blockedReason, showErrors, requiredError, required = true }) {
  const [otpSent, setOtpSent] = useState(false);
  const [seconds, setSeconds] = useState(59);
  const [sending, setSending] = useState(false);
  const [resending, setResending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpValue, setOtpValue] = useState("");

  useEffect(() => {
    if (!otpSent || seconds <= 0) return;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [otpSent, seconds]);

  const formatValid = phoneValid(value);
  const errKey = `${role}Phone`;

  const send = async (isResend) => {
    if (!formatValid) return toast.error("Enter a valid phone (10–15 digits)");
    if (blockedReason) return toast.error(blockedReason);
    isResend ? setResending(true) : setSending(true);
    const res = await sendParentPhoneOtp({ applicationNumber, role, phone: value.trim() });
    isResend ? setResending(false) : setSending(false);
    if (!res.ok) {
      toast.error(res.data?.detail || "Could not send code — please try again", { duration: 6000 });
      return;
    }
    if (!res.data?.delivered) {
      toast.error(
        res.data?.message ||
          "SMS could not be delivered to this number. Check the phone number and try again.",
        { duration: 8000 },
      );
      return;
    }
    setOtpSent(true);
    setOtpValue("");
    setSeconds(59);
    toast.success(`Code sent to ${value.trim()}`);
  };

  const verify = async () => {
    if (otpValue.length !== 6) return toast.error("Enter the 6-digit code");
    setVerifying(true);
    const res = await verifyParentPhoneOtp({ applicationNumber, role, otp: otpValue, phone: value.trim() });
    setVerifying(false);
    if (!res.ok) {
      toast.error(res.data?.detail || "Incorrect code");
      return;
    }
    setOtpSent(false);
    setOtpValue("");
    onVerified(value.trim());
    toast.success(`${label} verified`);
  };

  // Lets the candidate back out of a pending OTP to fix a typo'd number.
  const changePhone = () => {
    setOtpSent(false);
    setOtpValue("");
  };

  if (verified) {
    return (
      <div data-field={errKey}>
        <label className="mu-label">{label}</label>
        <div className="flex items-center justify-between gap-3 rounded-xl border border-green-200 bg-green-50 px-3 py-2.5" data-testid={`phone-verified-${role}`}>
          <div className="flex items-center gap-2 text-sm text-[#111] min-w-0 truncate">
            <CheckCircle2 className="h-4 w-4 text-green-700 shrink-0" /> {value}
          </div>
          <button type="button" onClick={onUnverify} className="text-xs font-semibold text-[#B60F2A] hover:underline shrink-0" data-testid={`btn-change-phone-${role}`}>
            Change number
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-field={errKey}>
      <label className="mu-label" htmlFor={`phone-${role}`}>{label}{required && <RequiredStar />}</label>
      <div className="flex gap-2">
        <input
          id={`phone-${role}`}
          type="tel"
          inputMode="numeric"
          data-testid={`input-phone-${role}`}
          className={`mu-input flex-1 ${otpSent ? "bg-[#F7F7F7] text-[#6B7280] cursor-not-allowed" : ""}`}
          placeholder="10-digit number"
          value={value}
          readOnly={otpSent}
          aria-readonly={otpSent}
          onChange={(e) => !otpSent && onChange(onlyDigits(e.target.value))}
        />
        {otpSent ? (
          <button
            type="button"
            className="mu-btn-secondary shrink-0"
            onClick={changePhone}
            data-testid={`btn-change-phone-pending-${role}`}
          >
            <PencilLine className="h-3.5 w-3.5" /> Change number
          </button>
        ) : (
          <button
            type="button"
            className="mu-btn-secondary shrink-0"
            disabled={sending || !formatValid || !!blockedReason}
            onClick={() => send(false)}
            data-testid={`btn-send-phone-otp-${role}`}
            title={blockedReason || ""}
          >
            {sending ? "Sending…" : "Send code"}
          </button>
        )}
      </div>
      {blockedReason && (
        <div className="text-[11px] text-amber-700 mt-1" data-testid={`phone-blocked-${role}`}>{blockedReason}</div>
      )}
      <ErrText showErrors={showErrors} errors={{ [errKey]: requiredError }} k={errKey} />

      {otpSent && (
        <div className="mt-3 rounded-xl border border-[#E5E7EB] bg-[#F7F7F7]/60 p-3" data-testid={`phone-otp-panel-${role}`}>
          <div className="text-xs text-[#6B7280] mb-2">Enter the 6-digit code sent to {value.trim()}</div>
          <InputOTP maxLength={6} value={otpValue} onChange={setOtpValue} data-testid={`phone-otp-input-${role}`}>
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
            </InputOTPGroup>
            <InputOTPSeparator />
            <InputOTPGroup>
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
          <div className="flex items-center justify-between mt-3">
            <div className="text-xs text-[#6B7280] flex items-center gap-1">
              <Timer className="h-3.5 w-3.5" />
              {seconds > 0 ? (
                <span>Resend in <span className="font-medium text-[#111]">00:{String(seconds).padStart(2, "0")}</span></span>
              ) : (
                <span>Didn&rsquo;t receive it?</span>
              )}
            </div>
            <button
              type="button"
              disabled={seconds > 0 || resending}
              onClick={() => send(true)}
              className="text-xs font-semibold text-[#B60F2A] disabled:text-[#9CA3AF] disabled:cursor-not-allowed hover:underline"
              data-testid={`btn-resend-phone-otp-${role}`}
            >
              {resending ? "Resending…" : "Resend code"}
            </button>
          </div>
          <button
            type="button"
            onClick={verify}
            disabled={verifying || otpValue.length !== 6}
            className="mu-btn-primary w-full mt-3"
            data-testid={`btn-verify-phone-otp-${role}`}
          >
            {verifying ? "Verifying…" : "Verify"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function ContactVerificationStep({ onNext }) {
  const { state, update, updateCandidate } = useRegistration();
  const c = state.candidate || {};
  const appNo = state.applicationNumber;
  const phoneVerified = state.phoneVerified || {};
  const [showErrors, setShowErrors] = useState(false);
  // The admissions-seeded roster row for this application — used to prefill
  // emails (candidate → mail_id, parent1/parent2 → father_mail/mother_mail
  // depending on relation). Fetched here rather than reused from Landing's own
  // lookup so this step works standalone (refresh, direct nav, etc.).
  const [roster, setRoster] = useState(null);

  useEffect(() => {
    if (!appNo) return;
    let cancelled = false;
    (async () => {
      const r = await verifyApplication(appNo);
      if (!cancelled && r?.exists) setRoster(r);
    })();
    return () => { cancelled = true; };
  }, [appNo]);

  // Prefill the candidate's own email once, from the roster — never overwrites
  // something already typed.
  useEffect(() => {
    if (!roster?.email || c.email) return;
    updateCandidate({ email: roster.email });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roster]);

  const rosterEmailForRelation = (relation) => {
    const rel = (relation || "").trim().toLowerCase();
    if (rel === "father") return roster?.father_mail || "";
    if (rel === "mother") return roster?.mother_mail || "";
    return "";
  };
  // True if `value` is either empty or still exactly one of the roster's known
  // father/mother emails — i.e. it's a machine-suggested guess, not something
  // the candidate typed themselves — so it's safe to replace with a fresh guess.
  const isAutoFillableValue = (value) => {
    const v = (value || "").trim();
    if (!v) return true;
    return v === (roster?.father_mail || "").trim() || v === (roster?.mother_mail || "").trim();
  };
  // Re-guesses the parent's email from the roster whenever their relation is
  // picked — including re-picking after an earlier relation already triggered
  // a guess, as long as the field still holds a machine-suggested value rather
  // than something the candidate typed themselves.
  useEffect(() => {
    const guess = rosterEmailForRelation(c.parent1Relation);
    if (guess && guess !== c.parent1Email && isAutoFillableValue(c.parent1Email)) {
      updateCandidate({ parent1Email: guess });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roster, c.parent1Relation]);
  useEffect(() => {
    const guess = rosterEmailForRelation(c.parent2Relation);
    if (guess && guess !== c.parent2Email && isAutoFillableValue(c.parent2Email)) {
      updateCandidate({ parent2Email: guess });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roster, c.parent2Relation]);

  const emails = { candidate: c.email || "", parent1: c.parent1Email || "", parent2: c.parent2Email || "" };

  const setEmail = (role, val) => {
    if (role === "candidate") updateCandidate({ email: val });
    else if (role === "parent1") updateCandidate({ parent1Email: val });
    else updateCandidate({ parent2Email: val });
  };

  const phones = { parent1: c.parent1Phone || "", parent2: c.parent2Phone || "" };
  const setPhone = (role, val) => {
    if (role === "parent1") updateCandidate({ parent1Phone: val });
    else updateCandidate({ parent2Phone: val });
  };
  const markPhoneVerified = (role) => (val) => {
    setPhone(role, val);
    update({ phoneVerified: { ...state.phoneVerified, [role]: true } });
  };
  const unverifyPhone = (role) => () => {
    update({ phoneVerified: { ...state.phoneVerified, [role]: false } });
  };

  // Father/Mother are mutually exclusive across the two parents — picking one
  // for parent1 leaves only the other choice for parent2, and vice versa.
  const relationOptions = (currentValue, otherValue) =>
    ["Father", "Mother"].filter((r) => r !== otherValue || r === currentValue);
  const setRelation = (role, value) => {
    if (role === "parent1") {
      updateCandidate({
        parent1Relation: value,
        ...(value && c.parent2Relation === value ? { parent2Relation: "" } : {}),
      });
    } else {
      updateCandidate({
        parent2Relation: value,
        ...(value && c.parent1Relation === value ? { parent1Relation: "" } : {}),
      });
    }
  };

  const parent2Touched = !!(c.parent2Name || c.parent2Phone || c.parent2Relation || c.parent2Email);

  const errors = useMemo(() => {
    const e = {};
    if (!c.email || !emailValid(c.email)) e.candidate = "Enter a valid email address";
    if (!c.parent1Name) e.parent1Name = REQ;
    if (!c.parent1Phone) e.parent1Phone = REQ;
    else if (!phoneValid(c.parent1Phone)) e.parent1Phone = PHONE_REQ;
    else if (!phoneVerified.parent1) e.parent1Phone = "Verify Parent 1's phone to continue";
    if (!c.parent1Relation) e.parent1Relation = REQ;
    if (!c.parent1Email || !emailValid(c.parent1Email)) e.parent1 = "Enter a valid email address";
    if (parent2Touched) {
      if (!c.parent2Name) e.parent2Name = REQ;
      if (!c.parent2Phone) e.parent2Phone = REQ;
      else if (!phoneValid(c.parent2Phone)) e.parent2Phone = PHONE_REQ;
      else if (!phoneVerified.parent2) e.parent2Phone = "Verify Parent 2's phone to continue";
      if (!c.parent2Relation) e.parent2Relation = REQ;
      if (!c.parent2Email || !emailValid(c.parent2Email)) e.parent2 = "Enter a valid email address";
    }
    return e;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.email, c.parent1Name, c.parent1Phone, c.parent1Relation, c.parent1Email, c.parent2Name, c.parent2Phone, c.parent2Relation, c.parent2Email, state.phoneVerified, parent2Touched]);

  const canNext = Object.keys(errors).length === 0;
  const errCls = (key) => (showErrors && errors[key] ? "mu-input border-red-400 focus:border-red-500 focus:ring-red-500" : "mu-input");

  const proceed = () => {
    if (!canNext) {
      setShowErrors(true);
      toast.error(`Please complete all required fields (${Object.keys(errors).length} pending)`);
      const first = Object.keys(errors)[0];
      const dataField = ["candidate", "parent1", "parent2"].includes(first) ? `email-${first}` : first;
      setTimeout(() => {
        const el = document.querySelector(`[data-field="${dataField}"]`);
        if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="mu-card p-5 bg-blue-50/50 border-blue-100" data-testid="contact-verify-note">
        <div className="flex items-start gap-3">
          <MailCheck className="h-5 w-5 text-blue-700 mt-0.5 shrink-0" />
          <p className="text-sm text-[#111] leading-relaxed">
            We&rsquo;ll send a one-time code to Parent 1&rsquo;s phone to confirm it&rsquo;s correct — Parent 2 is optional, but if you fill in any of their details, their phone must be verified too. Email addresses aren&rsquo;t verified with a code — please type them carefully.
          </p>
        </div>
      </div>

      <div className="mu-card p-6 md:p-8 space-y-4">
        <div className="tiny-label">Your Email</div>
        <EmailField
          role="candidate"
          label="Candidate Email"
          value={emails.candidate}
          onChange={(v) => setEmail("candidate", v)}
          showErrors={showErrors}
          requiredError={errors.candidate}
        />
      </div>

      <div className="mu-card p-6 md:p-8 space-y-4">
        <div className="tiny-label">Parent / Guardian 1 <span className="text-red-500">*</span></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div data-field="parent1Name">
            <label className="mu-label" htmlFor="p1name">Name<RequiredStar /></label>
            <input id="p1name" data-testid="input-parent1-name" className={errCls("parent1Name")} value={c.parent1Name || ""} onChange={(e) => updateCandidate({ parent1Name: e.target.value.toUpperCase() })} />
            <ErrText showErrors={showErrors} errors={errors} k="parent1Name" />
          </div>
          <div data-field="parent1Relation">
            <label className="mu-label" htmlFor="p1rel">Relation<RequiredStar /></label>
            <select id="p1rel" data-testid="input-parent1-relation" className={errCls("parent1Relation")} value={c.parent1Relation || ""} onChange={(e) => setRelation("parent1", e.target.value)}>
              <option value="">Select</option>
              {relationOptions(c.parent1Relation, c.parent2Relation).map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <ErrText showErrors={showErrors} errors={errors} k="parent1Relation" />
          </div>
        </div>
        <PhoneField
          role="parent1"
          label="Parent 1 Phone"
          value={phones.parent1}
          onChange={(v) => setPhone("parent1", v)}
          verified={!!phoneVerified.parent1}
          onVerified={markPhoneVerified("parent1")}
          onUnverify={unverifyPhone("parent1")}
          applicationNumber={appNo}
          blockedReason={!c.parent1Name || !c.parent1Relation ? "Fill in Parent 1's name and relation first" : null}
          showErrors={showErrors}
          requiredError={errors.parent1Phone}
        />
        <EmailField
          role="parent1"
          label="Parent 1 Email"
          value={emails.parent1}
          onChange={(v) => setEmail("parent1", v)}
          showErrors={showErrors}
          requiredError={errors.parent1}
        />
      </div>

      <div className="mu-card p-6 md:p-8 space-y-4">
        <div className="tiny-label">Parent / Guardian 2 <span className="text-[#9CA3AF] normal-case font-normal">(optional — fill in any field to make the group required)</span></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div data-field="parent2Name">
            <label className="mu-label" htmlFor="p2name">Name</label>
            <input id="p2name" data-testid="input-parent2-name" className={errCls("parent2Name")} value={c.parent2Name || ""} onChange={(e) => updateCandidate({ parent2Name: e.target.value.toUpperCase() })} />
            <ErrText showErrors={showErrors} errors={errors} k="parent2Name" />
          </div>
          <div data-field="parent2Relation">
            <label className="mu-label" htmlFor="p2rel">Relation</label>
            <select id="p2rel" data-testid="input-parent2-relation" className={errCls("parent2Relation")} value={c.parent2Relation || ""} onChange={(e) => setRelation("parent2", e.target.value)}>
              <option value="">Select</option>
              {relationOptions(c.parent2Relation, c.parent1Relation).map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <ErrText showErrors={showErrors} errors={errors} k="parent2Relation" />
          </div>
        </div>
        <PhoneField
          role="parent2"
          label="Parent 2 Phone"
          value={phones.parent2}
          onChange={(v) => setPhone("parent2", v)}
          verified={!!phoneVerified.parent2}
          onVerified={markPhoneVerified("parent2")}
          onUnverify={unverifyPhone("parent2")}
          applicationNumber={appNo}
          blockedReason={!c.parent2Name || !c.parent2Relation ? "Fill in Parent 2's name and relation first" : null}
          showErrors={showErrors && parent2Touched}
          requiredError={errors.parent2Phone}
          required={parent2Touched}
        />
        <EmailField
          role="parent2"
          label="Parent 2 Email"
          value={emails.parent2}
          onChange={(v) => setEmail("parent2", v)}
          showErrors={showErrors && parent2Touched}
          requiredError={errors.parent2}
          required={parent2Touched}
        />
      </div>

      <div className="flex items-center justify-between gap-4">
        {showErrors && !canNext && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 inline-flex items-center gap-2">
            <AlertCircle className="h-3.5 w-3.5" />
            {Object.keys(errors).length} required field{Object.keys(errors).length > 1 ? "s" : ""} still incomplete.
          </div>
        )}
        <button onClick={proceed} className="mu-btn-primary ml-auto" data-testid="btn-next-contact">
          Continue <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
